import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { api } from "../lib/api";
import { Menu, X, LogOut, Swords } from "lucide-react";

const LINKS = [
  { to: "/", key: "nav.game" },
  { to: "/discord", key: "nav.discord" },
  { to: "/lagofast", key: "nav.lagofast" },
  { to: "/classifica", key: "nav.classifica" },
  { to: "/youtube", key: "nav.youtube" },
  { to: "/news", key: "nav.news" },
  { to: "/streamer", key: "nav.streamers" },
  { to: "/eventi", key: "nav.eventi" },
  { to: "/giveaway", key: "nav.giveaway" },
  { to: "/donazione", key: "nav.donazione" },
];

const FlagIT = () => (
  <svg viewBox="0 0 24 16" className="w-5 h-3.5 block"><rect width="8" height="16" fill="#009246" /><rect x="8" width="8" height="16" fill="#F4F5F0" /><rect x="16" width="8" height="16" fill="#CE2B37" /></svg>
);

const FlagEN = () => (
  <svg viewBox="0 0 24 16" className="w-5 h-3.5 block"><rect width="24" height="16" fill="#012169" /><path d="M0 0L24 16M24 0L0 16" stroke="#F4F5F0" strokeWidth="3" /><path d="M12 0V16M0 8H24" stroke="#F4F5F0" strokeWidth="5" /><path d="M12 0V16M0 8H24" stroke="#C8102E" strokeWidth="2.5" /></svg>
);

function LangSwitcher() {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-[#1A1A1A]" data-testid="lang-switcher">
      <button
        onClick={() => setLang("it")}
        data-testid="lang-it-button"
        aria-label="Italiano"
        className={`p-1.5 transition-all duration-300 ${lang === "it" ? "opacity-100 bg-wardogs-dim" : "opacity-40 hover:opacity-90"}`}
      >
        <FlagIT />
      </button>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en-button"
        aria-label="English"
        className={`p-1.5 transition-all duration-300 ${lang === "en" ? "opacity-100 bg-wardogs-dim" : "opacity-40 hover:opacity-90"}`}
      >
        <FlagEN />
      </button>
    </div>
  );
}

function CountdownBar({ to, label }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(to).getTime() - now);
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  const cell = (v, u) => (
    <span key={u} className="flex items-baseline gap-1">
      <span
        className="font-display text-2xl md:text-3xl leading-none text-white transition-transform duration-300 group-hover:scale-105"
        style={{ textShadow: "0 0 16px rgba(255,106,0,0.6)" }}
      >
        {String(v).padStart(2, "0")}
      </span>
      <span className="font-mono text-[9px] uppercase tracking-widest text-wardogs">{u}</span>
    </span>
  );
  const sep = <span className="font-display text-2xl md:text-3xl leading-none text-wardogs animate-blink">:</span>;
  return (
    <div className="border-b border-wardogs/30 bg-[#0C0805]" data-testid="countdown-bar">
      <a
        href="https://store.steampowered.com/app/1867240/WARDOGS/"
        target="_blank"
        rel="noopener noreferrer"
        className="group max-w-7xl mx-auto px-5 md:px-8 h-12 flex items-center justify-center gap-3 md:gap-4"
        data-testid="countdown-bar-link"
        title="Wishlist WARDOGS su Steam"
      >
        <Swords className="w-4 h-4 text-wardogs shrink-0 hidden sm:block transition-transform duration-300 group-hover:rotate-12" />
        <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-wardogs hidden md:inline" data-testid="countdown-bar-label">
          {label || "WARDOGS — Early Access"}
        </span>
        <span className="flex items-center gap-2 md:gap-2.5" data-testid="countdown-bar-timer">
          {cell(d, "g")}{sep}{cell(h, "h")}{sep}{cell(m, "m")}{sep}{cell(s, "s")}
        </span>
        <Swords className="w-4 h-4 text-wardogs shrink-0 hidden sm:block -scale-x-100 transition-transform duration-300 group-hover:-rotate-12" />
      </a>
    </div>
  );
}

export function Nav() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("/logo-wolf.png");
  const [countdown, setCountdown] = useState(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (data.logo_url) setLogoUrl(data.logo_url);
      // La barra in cima segue SEMPRE la data di uscita di WARDOGS (fissa) — il countdown OBS è una cosa separata
      setCountdown({ to: data.release_date || "2026-09-10T16:00:00.000Z", label: "" });
    }).catch(() => {});
  }, []);

  const links = [...LINKS];
  if (user?.is_staff) links.push({ to: "/admin", key: "nav.staff", staff: true });

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-[#1A1A1A]">
      {countdown && <CountdownBar to={countdown.to} label={countdown.label} />}
      <div className="max-w-[1600px] mx-auto px-5 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img src={logoUrl} alt="WARDOGS" className="w-11 h-11 self-center object-contain transition-transform duration-500 group-hover:scale-110" />
          <span className="font-display text-2xl leading-none tracking-wide text-white self-center whitespace-nowrap">
            WARDOGS<span className="text-wardogs">.HQ</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6 min-w-0">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              style={{ wordSpacing: "-0.14em" }}
              className={({ isActive }) =>
                `font-mono text-[12px] uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300 flex items-center gap-1.5 ${
                  isActive ? "text-wardogs" : l.staff ? "text-manticore hover:text-white" : "text-steel hover:text-white"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <LangSwitcher />
          {user ? (
            <div className="flex items-center gap-3" data-testid="nav-user-chip">
              <Link to="/profilo" data-testid="nav-profile-link" className="flex items-center gap-2 group">
                {user.avatar && (
                  <img src={user.avatar} alt={user.display_name} className="w-8 h-8 chamfer-sm border border-[#1A1A1A]" />
                )}
                <span className="font-mono text-xs text-white group-hover:text-wardogs transition-colors duration-300">{user.display_name}</span>
              </Link>
              <button
                data-testid="nav-logout-button"
                onClick={logout}
                className="text-steel hover:text-valkyra transition-colors duration-300"
                aria-label={t("nav.logout")}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Link
              to="/profilo"
              data-testid="nav-login-button"
              className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300"
            >
              {t("nav.login")}
            </Link>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[#1A1A1A] bg-[#050505] px-5 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.25em] ${isActive ? "text-wardogs" : l.staff ? "text-manticore" : "text-steel"}`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
          {user && (
            <NavLink to="/profilo" data-testid="nav-mobile-profile-link" className="font-mono text-xs uppercase tracking-[0.25em] text-white">
              {user.display_name}
            </NavLink>
          )}
          <div className="flex items-center gap-3">
            <LangSwitcher />
          </div>
          {user ? (
            <button data-testid="nav-mobile-logout-button" onClick={logout} className="text-left font-mono text-xs uppercase tracking-[0.25em] text-valkyra">
              {t("nav.logout")} ({user.display_name})
            </button>
          ) : (
            <Link to="/profilo" data-testid="nav-login-button-mobile" className="text-left font-mono text-xs uppercase tracking-[0.25em] text-[#9146FF]">
              {t("nav.login")}
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
